import React, { useEffect } from 'react';
import axios from 'axios';
import {
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Grid,
} from '@material-ui/core';
import { useMediaQuery } from 'react-responsive';
import Chart from 'react-apexcharts';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { useHistory } from 'react-router-dom';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import { storeContext } from '../provider/Provider';
import useStyles from './Leads.style';
import { leadsActions } from '../../store/leads-reducer';
import InfoCard from '../info-card/InfoCard';
import { StyledTableHeadCellLeads, StyledTableCell } from '../../customUI/table/TableStyles';
import { formatPhoneNumber, getGrowthPercentage } from '../../functions';
import chartOptions from '../../customUI/ChartOptions';
import MobileTitle from '../../customUI/MobileTitle';

const { REACT_APP_FLASK_API_URL } = process.env;

const Leads = () => {
  const isPhone = useMediaQuery({ query: '(max-width: 768px)' });
  const classes = useStyles();
  const { state, dispatch } = React.useContext(storeContext);
  const history = useHistory();

  const handleViewAllButtonClick = () => {
    history.push('/leads/full-view');
  };

  const handleViewAllCallsButtonClick = () => {
    history.push('/calls/full-view');
  };

  useEffect(() => {
    dispatch({
      type: leadsActions.LEADS_SET_ALL,
      payload: {
        isFetching: true,
      },
    });
    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/leads/${state.websiteSwitch.currentWebsite}/new/1/15?marked_as_lead=true&ordering=-updated_at`)
      .then((res) => {
        dispatch({
          type: leadsActions.LEADS_SET_ALL,
          payload: {
            leads: res.data.data.leads,
            total: res.data.data.total,
          },
        });
      });
    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/leads/${state.websiteSwitch.currentWebsite}/count`)
      .then((res) => {
        dispatch({
          type: leadsActions.LEADS_SET_ALL,
          payload: {
            isFetching: false,
            allLeadsCount: res.data.data.allLeadsCount,
            currentPeriodLeadsCount: res.data.data.leadsCurrentMonth,
            prevPeriodLeadsCount: res.data.data.leadsLastMonth,
            leadsByMonth: res.data.data.leadsByMonth ? JSON.parse(res.data.data.leadsByMonth) : {},
          },
        });
      });
    axios
      .get(
        `${REACT_APP_FLASK_API_URL}/v1/get-call-metrics-totals/${state.websiteSwitch.currentWebsite}`
      )
      .then((res) => {
        dispatch({
          type: leadsActions.LEADS_SET_ALL,
          payload: {
            allPhoneCallsCount: res.data.total_count,
            callsByStatusCurrentPeriod: res.data.callsCurrentMonthByStatus,
            currentPeriodCallsCount: res.data.callsCurrentMonth,
            prevPeriodCallsCount: res.data.callsLastMonth,
            callsByMonth: res.data.callsByMonth ? JSON.parse(res.data.callsByMonth) : {},
          },
        });
      });
    axios
      .get(
        `${REACT_APP_FLASK_API_URL}/v1/get-call-metrics/${state.websiteSwitch.currentWebsite}/1/15`
      )
      .then((res) => {
        dispatch({
          type: leadsActions.LEADS_SET_ALL,
          payload: {
            isFetching: false,
            calls: res.data.message,
          },
        });
      });
      axios
      .get(
        `${REACT_APP_FLASK_API_URL}/v1/get-call-metrics/${state.websiteSwitch.currentWebsite}/1/15?status=-no answer,-failed`
      )
      .then((res) => {
        dispatch({
          type: leadsActions.LEADS_SET_ALL,
          payload: {
            isFetching: false,
            answeredCalls: res.data.message,
          },
        });
      });
  }, []);
  if (state.leads.isFetching) {
    return (
      <LinearProgress
        style={{ width: '100%' }}
        classes={{
          colorPrimary: classes.colorPrimary,
          barColorPrimary: classes.barColorPrimary,
        }}
      />
    );
  }
  const donutChartOptions = { ...chartOptions, labels: state.leads.callsByStatusChartLabels };
  donutChartOptions.colors = ['#2A5CA9', '#B83A94', '#00B5E2'];
  return (
    <>
      <div className={classes.wrapper}>
        {isPhone && <MobileTitle title="Leads" />}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {state.leads.allPhoneCallsCount && state.leads.allPhoneCallsCount.toString() !== '0' ? (
              <InfoCard
                percentage={getGrowthPercentage(
                  state.leads.prevPeriodCallsCount,
                  state.leads.currentPeriodCallsCount,
                  0
                ).toString()}
                name="Total Organic Calls"
                value={state.leads.currentPeriodCallsCount.toString()}
                explanation="from last 28 days"
                isFetching={state.leads.isFetching}
                labels={state.leads.callsByMonthChartLabels}
                historyValues={state.leads.callsByMonthChartSeries}
                inlineTooltip
                reversed="align-left"
                tooltip="Total Calls = the total number of organic phone calls during the current month. Chart = indicates the increase or decrease in number of calls compared to the previous 4 weeks."
              />
            ) : (
              <div
                className={classes.card}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 140,
                  padding: 24,
                  paddingBottom: 10,
                }}
              >
                <div style={{ fontSize: 19, color: '#58595B' }}>CTM required for this feature.</div>
              </div>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {state.leads.leadsByMonth ? (
              <InfoCard
                percentage={getGrowthPercentage(
                  state.leads.prevPeriodLeadsCount,
                  state.leads.currentPeriodLeadsCount,
                  0
                ).toString()}
                name="Total Lead Forms"
                explanation="from last 28 days"
                value={state.leads.currentPeriodLeadsCount.toString()}
                isFetching={state.leads.isFetching}
                labels={state.leads.leadsByMonthChartLabels}
                historyValues={state.leads.leadsByMonthChartSeries}
                inlineTooltip
                reversed="align-left"
                tooltip="Total Leads = the total number of all leads during the current month. Chart = indicates the increase or decrease in number of all leads compared to the previous 4 weeks."
              />
            ) : (
              <div
                className={classes.card}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 140,
                  padding: 24,
                  paddingBottom: 10,
                }}
              >
                <div style={{ fontSize: 19, color: '#58595B' }}>
                  Please contact your Hennessey Digital Account Manager to learn more about sending
                  leads through the Platform.
                </div>
              </div>
            )}
          </Grid>
        </Grid>
      </div>
      <div className={classes.wrapper}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5} style={{ display: 'none'}}>
            <div
              className={classes.card}
              style={{
                marginBottom: 20,
              }}
            >
              <div className={classes.card__wrapper}>
                <div className={classes.heading}>Calls By Status</div>
                {state.leads.callsByStatusChartSeries.length === 0 ? (
                  <div className={classes.noContentBlock}>
                    <span>No calls in this section</span>
                  </div>
                ) : (
                  <Chart
                    options={donutChartOptions}
                    series={state.leads.callsByStatusChartSeries}
                    type="donut"
                    height={400}
                  />
                )}
              </div>
            </div>
            <div className={classes.table__card}>
              <div className={classes.card__wrapper}>
                <div className={classes.heading}>Call Conversions</div>
                <Table aria-label="Call Conversions table">
                  <TableHead>
                    <TableRow>
                      <StyledTableHeadCellLeads align="left">Phone Number</StyledTableHeadCellLeads>
                      <StyledTableHeadCellLeads align="left">Status</StyledTableHeadCellLeads>
                      <StyledTableHeadCellLeads>Duration</StyledTableHeadCellLeads>
                      <StyledTableHeadCellLeads>Audio</StyledTableHeadCellLeads>
                    </TableRow>
                  </TableHead>
                  {state.leads.answeredCalls ? (
                    <TableBody>
                      {state.leads.answeredCalls.map((row) => (
                        <TableRow key={row.id}>
                          <StyledTableCell component="td" scope="row">
                            <a
                              className={classes.blackLink}
                              href={`tel:${row.caller_number_complete.replace(/[^\\d]/g, '')}`}
                            >
                              {formatPhoneNumber(row.caller_number_complete)}
                            </a>
                          </StyledTableCell>
                          <StyledTableCell align="left">{row.status}</StyledTableCell>
                          <StyledTableCell component="td" scope="row">
                            {new Date(row.duration * 1000).toISOString().substr(11, 8)}
                          </StyledTableCell>
                          <StyledTableCell component="td" scope="row">
                            {row.audio ? (
                              <PlayCircleOutlineIcon
                                style={{ cursor: 'pointer', color: '#38C0F0' }}
                                onClick={() => window.open(row.audio, '_blank')}
                              />
                            ) : (
                              <span style={{ cursor: 'not-allowed', color: '#999999' }}>N/A</span>
                            )}
                          </StyledTableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  ) : (
                    <></>
                  )}
                </Table>
                {!state.leads.calls.length && (
                  <div className={classes.noContentBlock}>
                    <span>No Calls</span>
                  </div>
                )}
              </div>
              <Divider />
              <div className={classes.card__wrapper}>
                <div
                  className={classes.cardActionButton}
                  onClick={handleViewAllCallsButtonClick}
                  aria-hidden="true"
                >
                  <div className={classes.viewAllText}>View All</div>
                  <ArrowForwardIosIcon style={{ paddingLeft: 10, fontSize: '11px' }} />
                </div>
              </div>
            </div>
          </Grid>
          <Grid item xs={12} md={12}>
            <div className={classes.table__card}>
              <div className={classes.card__wrapper}>
                <div className={classes.heading}>All Leads</div>
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      {isPhone ? (
                        <>
                          <StyledTableHeadCellLeads>Name</StyledTableHeadCellLeads>
                          <StyledTableHeadCellLeads>Email</StyledTableHeadCellLeads>
                          <StyledTableHeadCellLeads>Type</StyledTableHeadCellLeads>
                        </>
                      ) : (
                        <>
                          <StyledTableHeadCellLeads>Name</StyledTableHeadCellLeads>
                          <StyledTableHeadCellLeads>Email</StyledTableHeadCellLeads>
                          <StyledTableHeadCellLeads>Phone Number</StyledTableHeadCellLeads>
                          <StyledTableHeadCellLeads align="center">Type</StyledTableHeadCellLeads>
                          <StyledTableHeadCellLeads align="center">
                            Channel
                          </StyledTableHeadCellLeads>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  {state.leads.total ? (
                    <TableBody>
                      {state.leads.leads.map((row) => (
                        <TableRow key={row.uuid}>
                          {isPhone ? (
                            <>
                              <StyledTableCell component="td" scope="row">
                                {row.leadBody.fullName}
                              </StyledTableCell>
                              <StyledTableCell component="td" scope="row">
                                <a
                                  className={classes.blackLink}
                                  href={`mailto:${row.leadBody.email}`}
                                >
                                  {row.leadBody.email && (
                                    <>
                                      {row.leadBody.email.length < 20
                                        ? row.leadBody.email
                                        : `${row.leadBody.email.slice(0, 15)}..`}
                                    </>
                                  )}
                                </a>
                              </StyledTableCell>
                              <StyledTableCell align="center">
                                {row.leadBody.chat ? <span>Chat</span> : <span>Form</span>}
                              </StyledTableCell>
                            </>
                          ) : (
                            <>
                              <StyledTableCell component="td" scope="row">
                                {row.leadBody.fullName}
                              </StyledTableCell>
                              <StyledTableCell component="td" scope="row">
                                <a
                                  className={classes.blackLink}
                                  href={`mailto:${row.leadBody.email}`}
                                >
                                  {row.leadBody.email}
                                </a>
                              </StyledTableCell>
                              <StyledTableCell>
                                {row.leadBody.phone ? (
                                  <a
                                    className={classes.blackLink}
                                    href={`tel:${row.leadBody.phone.replace(/[^\\d]/g, '')}`}
                                  >
                                    {formatPhoneNumber(row.leadBody.phone)}
                                  </a>
                                ) : (
                                  '-'
                                )}
                              </StyledTableCell>
                              <StyledTableCell align="center">
                                {row.leadBody.chat ? <span>Chat</span> : <span>Form</span>}
                              </StyledTableCell>
                              <StyledTableCell />
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  ) : (
                    <></>
                  )}
                </Table>
                {!state.leads.leads && (
                  <div className={classes.noContentBlock}>
                    <span>No Leads</span>
                  </div>
                )}
              </div>
              <Divider />
              <div className={classes.card__wrapper}>
                <div
                  className={classes.cardActionButton}
                  onClick={handleViewAllButtonClick}
                  aria-hidden="true"
                >
                  <div className={classes.viewAllText}>View All</div>
                  <ArrowForwardIosIcon style={{ paddingLeft: 10, fontSize: '11px' }} />
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default Leads;
